import { useEffect, useState } from "react";
import { Alert } from "@/components/common/Alert";
import { Button } from "@/components/common/Button";
import { Modal } from "@/components/common/Modal";

interface UnlockSettlementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  isLoading: boolean;
  /** Komunikat błędu z backendu (403/400/404) — bez niego nieudane cofnięcie wygląda jak brak reakcji. */
  submitError?: string | null;
}

const MIN_REASON_LENGTH = 5;
const MAX_REASON_LENGTH = 500;

export function UnlockSettlementModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  submitError,
}: UnlockSettlementModalProps) {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setReason("");
      setTouched(false);
    }
  }, [isOpen]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_REASON_LENGTH;
  const tooLong = reason.length > MAX_REASON_LENGTH;
  const showError = touched && (tooShort || tooLong);
  const errorMessage = tooShort
    ? `Powód musi mieć co najmniej ${MIN_REASON_LENGTH} znaków.`
    : tooLong
      ? `Powód nie może przekraczać ${MAX_REASON_LENGTH} znaków.`
      : "";

  function handleSubmit() {
    setTouched(true);
    if (tooShort || tooLong) return;
    onSubmit(trimmed);
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cofnij rozliczenie do edycji">
      <div className="space-y-4">
        <p className="text-sm text-sub">
          Rozliczenie wróci do statusu <strong>Obliczone</strong>. Będziesz mógł
          dodać lub poprawić wpisy. Agent otrzyma mail z aktualizacją dopiero po
          ponownym zatwierdzeniu — nie informujemy go o samym cofnięciu.
        </p>

        {submitError && (
          <Alert variant="error" title="Nie udało się cofnąć rozliczenia">
            {submitError}
          </Alert>
        )}

        <div>
          <label
            htmlFor="unlock-reason"
            className="mb-1 block text-sm font-medium text-main"
          >
            Powód korekty <span className="text-red-500">*</span>
          </label>
          <textarea
            id="unlock-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            rows={4}
            maxLength={MAX_REASON_LENGTH}
            placeholder="np. Dopisanie polisy TUW z raportu 891 (otrzymanego po zatwierdzeniu)"
            className="w-full rounded-md border border-edge-hard bg-panel px-3 py-2 text-sm text-main placeholder:text-faint focus:border-adara-500 focus:outline-none focus:ring-1 focus:ring-adara-500"
            disabled={isLoading}
          />
          <div className="mt-1 flex justify-between text-xs">
            <span className={showError ? "text-red-600" : "text-faint"}>
              {showError ? errorMessage : `Min. ${MIN_REASON_LENGTH} znaków`}
            </span>
            <span className="text-faint">
              {reason.length} / {MAX_REASON_LENGTH}
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Anuluj
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit}
            isLoading={isLoading}
            disabled={isLoading || tooShort || tooLong}
          >
            Cofnij rozliczenie
          </Button>
        </div>
      </div>
    </Modal>
  );
}
